'use client'

import React from 'react'
import { Box, Flex, SimpleGrid, Stack, Text, Title } from '@mantine/core'
import { motion } from 'framer-motion'
import { staggerContainer, staggerItem, textFadeInVariantsNoTransition } from '@/lib/animations'
import { MotionBox } from '@/components/shared/AnimatedComponents'
import type { SkuComponentConfigMap } from '@/types/enums/sku-component-types'

// Default copy rendered when no CMS `data` is supplied (e.g. refreshed lab pages).
const INSIGHTS_DATA = {
    title: 'Complete insights in days',
    steps: [
        {
            title: 'Book your test',
            description:
                'Instantly schedule at 2,000+ labs nationwide, at a time that works for you. Most visits take 15 minutes or less, with both walk-ins and appointments available.',
        },
        {
            title: 'Get clinician-reviewed results',
            description:
                'Your results are delivered quickly\u2014and reviewed by medical professionals. You\u2019ll get clear explanations, robust health insights, and recommended next steps tailored to your health needs.',
        },
        {
            title: 'Take action with physician guidance',
            description:
                'Choose to review your results and manage your plan with a General Medicine physician\u2014or simply use the insights on your own.',
        },
        {
            title: 'Track progress over time',
            description:
                'Repeat annually to see trends, improvements, and new emerging risks or book individual tests that focus on your set goals',
        },
    ],
}

export function InsightsWidget({ config }: { config?: SkuComponentConfigMap['insightsWidget'] }) {
    const insights = config?.data ?? INSIGHTS_DATA
    return (
        <Box
            component={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px 0px' }}
            variants={staggerContainer}
            maw={1440}
            mx="auto"
            w="100%"
            p={{ base: 24, sm: 60 }}
        >
            <MotionBox variants={textFadeInVariantsNoTransition}>
                <Title
                    order={2}
                    ta={{ base: 'left', sm: 'center' }}
                    mt={0}
                    mb={{ base: 32, sm: 60 }}
                    c="gray.9"
                    fz={{ base: 32, sm: 56 }}
                    fw={400}
                    lh="100%"
                    lts={{ base: -1.28, sm: -2.24 }}
                >
                    {insights.title}
                </Title>
            </MotionBox>

            <MotionBox
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-50px 0px' }}
            >
                <SimpleGrid
                    cols={{ base: 1, sm: 2, lg: 4 }}
                    spacing={{ base: 32, sm: 48 }}
                >
                    {insights.steps.map((step, index) => (
                        <MotionBox key={index} variants={staggerItem}>
                            <Flex direction="column" gap={{ base: 20, sm: 32 }}>
                                <Flex
                                    align="center"
                                    justify="center"
                                    w={{ base: 44, sm: 52.5 }}
                                    h={{ base: 44, sm: 52.5 }}
                                    bg="blue.6"
                                    style={{ borderRadius: '50%', flexShrink: 0 }}
                                >
                                    <Text
                                        c="gray.0"
                                        fz={{ base: 20, sm: 25.2 }}
                                        fw="var(--weight-display-small, 450)"
                                        lh="100%"
                                        lts={-0.252}
                                    >
                                        {index + 1}
                                    </Text>
                                </Flex>

                                <Stack gap={8}>
                                    <Text
                                        c="gray.9"
                                        fz={{ base: 20, sm: 24 }}
                                        fw="var(--weight-display-small, 450)"
                                        lh="100%"
                                        lts={-0.24}
                                    >
                                        {step.title}
                                    </Text>
                                    <Text
                                        c="#181818"
                                        fz={{ base: 14, sm: 16 }}
                                        fw="var(--weight-text, 450)"
                                        lh="150%"
                                    >
                                        {step.description}
                                    </Text>
                                </Stack>
                            </Flex>
                        </MotionBox>
                    ))}
                </SimpleGrid>
            </MotionBox>
        </Box>
    )
}
